import {
  type ProviderAvatarBlockProps,
  ProviderAvatarBlock,
  DateFormat,
  SkeletonLoader,
} from '@equip.health/ui';
import { DateTime } from 'luxon';
import { getLatestOutOfOfficeEndDate } from '~/lib/util/profile.util';
import { getProviderStateAtActionTime } from '~/lib/util/user.util';
import { useGetProviderInfoWithHistoryByIdQuery } from '~/store/services/providers';

interface AvatarBlockTooltipProps {
  actionTime?: string;
  isRoleAtAction?: boolean;
  providerExternalId: string;
  TOP_LEVEL_ID: string;
  shouldShowDisplayName?: boolean;
}

const ProviderAvatarBlockTooltip = ({
  actionTime,
  providerExternalId,
  TOP_LEVEL_ID,
  isRoleAtAction = false,
  shouldShowDisplayName = true,
}: AvatarBlockTooltipProps) => {
  const { data: providerData, isLoading: isProviderLoading } =
    useGetProviderInfoWithHistoryByIdQuery(
      {
        includeAvatar: true,
        includeOutOfOffice: true,
        includeProviderHistory: !!isRoleAtAction,
        providerExternalId,
      },
      { skip: !providerExternalId },
    );

  const legalName: ProviderAvatarBlockProps['legalName'] = {
    firstName: providerData?.firstName,
    lastName: providerData?.lastName,
    middleName: providerData?.middleName,
    suffix: providerData?.suffix,
  };

  const displayName: ProviderAvatarBlockProps['displayName'] = {
    firstName: providerData?.displayFirstName,
    lastName: providerData?.displayLastName,
    middleName: providerData?.displayMiddleName,
    suffix: providerData?.displaySuffix,
  };

  const isDisplayName = displayName.firstName && displayName.lastName;

  const credentials = providerData?.credentialsDetails.map(
    (credential) => credential.code,
  );

  const oooEndDate = getLatestOutOfOfficeEndDate(providerData?.outOfOffice);
  const deactivatedDate = DateTime.fromISO(
    providerData?.deactivatedDate,
  ).startOf('day');
  const deactivatedDateFormatted = deactivatedDate?.isValid
    ? deactivatedDate.toFormat(DateFormat.fullMonthDayYear)
    : '';

  const pronouns = providerData?.pronounsDetails.map((pronoun) => pronoun.name);

  const providerState =
    isRoleAtAction &&
    getProviderStateAtActionTime(providerData?.providerHistory, actionTime);

  const providerType =
    providerState?.providerTypeDetails?.name ||
    providerData?.providerTypeDetails?.name;

  return (
    <SkeletonLoader
      id={`${TOP_LEVEL_ID}__loader`}
      isLoaded={!isProviderLoading}
    >
      <ProviderAvatarBlock
        avatarUrl={providerData?.avatar?.thumbnailUrl}
        credentials={credentials}
        deactivatedDate={deactivatedDateFormatted}
        displayName={shouldShowDisplayName && isDisplayName && displayName}
        id={`${TOP_LEVEL_ID}__button-content`}
        legalName={legalName}
        oooDate={oooEndDate}
        otherPronoun={providerData?.otherPronounText}
        pronouns={pronouns}
        providerTypeName={providerType}
        shouldShowCredentials
        shouldShowDeactivatedDate
        shouldShowOOOIcon={oooEndDate !== null}
        shouldShowPronouns
        shouldShowProviderType
        variant="standard"
      />
    </SkeletonLoader>
  );
};

export default ProviderAvatarBlockTooltip;
